import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {
  palette,
  spacing,
  radius,
  shadow,
  transition,
  typography,
  gradient,
  easing,
  duration,
  zIndex,
  layout,
  status,
} from '../../../../style-utils/theme';
import {
  fadeIn,
  fadeInUp,
  scaleIn,
  stagger,
} from '../../../../style-utils/motion';
import { up, down, touch } from '../../../../style-utils/media-queries';
import { InlineMessage, m } from '../../../../motion';
import { RowButton, Td } from '../dashboard-units-components/dashboard-units.styled';

export const DashboardContent = styled.div<{ isAddNewBuilding?: boolean }>`
  box-sizing: border-box;
  height: 100%;
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: ${({ isAddNewBuilding }) =>
    isAddNewBuilding ? palette.surface : 'transparent'};
  overflow: ${({ isAddNewBuilding }) =>
    isAddNewBuilding ? 'hidden' : 'visible'};
  animation: ${fadeIn} ${duration.base} ${easing.standard} both;
`;

// A standing notice above the list — the buildings whose location, and so
// whose time zone, nobody has confirmed. Status tint, not an error: nothing is
// broken, something is unfinished.
export const ListingNotice = styled.p`
  margin: 0 0 ${spacing.sm};
  padding: ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${status.warning.bg};
  color: ${status.warning.fg};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
`;

export const BuildingsListing = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${spacing.sm};
  padding: ${spacing.sm};
  margin-bottom: ${spacing.sm};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.xs};
  animation: ${fadeInUp} ${duration.base} ${easing.entrance} both;

  ${up('md')} {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    padding: ${spacing.sm} ${spacing.md};

    /* first-CHILD, not first-of-type: the div title and the button action are
       each ":first-of-type" of their own tag, so the button also carried
       margin-right: auto and left the row's right edge.
       And it never shrinks: the controls beside it are one ListingControls
       group, which wraps under the title rather than squeezing it to a column
       of single letters (dashboard.styled.tsx says why). */
    & > *:first-child {
      flex: 0 0 auto;
      margin-right: auto;
    }
  }

  ${down('md')} {
    & > * {
      width: 100%;
    }
    & input {
      width: 100%;
      box-sizing: border-box;
    }
  }
`;

// Holds the "All portfolios" filter beside the buildings search. Full width on
// a phone like every other child of BuildingsListing, then a fixed control from
// tablet up.
//
// Deliberately NO size or radius override on the select. The neighbour on this
// screen is StyledInput from login.styled (fieldHeight 48, radius.xs) — not the
// rounded search pill it looks like — and StyledBuildingSelect already matches
// that exactly. An earlier controlHeight/pill override made the filter 4px
// shorter and fully rounded against a square field: the very mismatch it was
// written to avoid.
export const PortfolioFilterContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  width: 100%;

  ${up('md')} {
    flex: 0 0 auto;
    width: auto;
    min-width: 200px;
  }
`;

export const Title = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

/* RESTORE (QA-09). Secondary, matching RestoreButton on the People screen — the
   two screens do the same thing and must not look like different features. */
export const RestoreBuildingButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${layout.touchTarget};
  padding: 0 ${spacing.md};
  cursor: pointer;
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.white};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: 14px;
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;
  transition: ${transition.colors};

  &:hover:not(:disabled) {
    background: ${palette.porcelain};
  }
  &:disabled {
    cursor: default;
    color: ${palette.slate};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const NewBuildingButton = styled.button`
  -webkit-appearance: none;
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  height: ${layout.touchTarget};
  padding: 0 ${spacing.md};
  border: none;
  border-radius: ${radius.sm};
  background: ${gradient.primary};
  color: ${palette.white};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  white-space: nowrap;
  cursor: pointer;
  box-shadow: ${shadow.brand};
  transition: ${transition.colors},
    transform ${duration.fast} ${easing.standard};

  ${up('lg')} {
    height: ${layout.controlHeight};
  }

  &:hover {
    background: ${gradient.primaryHover};
    box-shadow: ${shadow.brandHover};
    transform: translateY(-1px);
  }

  &:active {
    transform: scale(0.97);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.brand}, ${shadow.focus};
  }
`;

export const Column = styled.div<{ isCentered?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
`;

export const Row = styled.div<{ isCentered?: boolean }>`
  display: flex;
  flex: 1;
  ${({ isCentered }) =>
    isCentered &&
    `
    align-items: center;
    justify-content: center;
  `}
`;

export const StyledFlexRow = styled(Row)<{
  index?: number;
  isHeaderRow?: boolean;
  isActionsOpen?: boolean;
  clickable?: boolean;
}>`
  flex: 0 0 auto;
  width: 100%;
  margin-bottom: ${spacing.xs};

  /* Set only for a row that actually opens the property. A role without
     building:read gets an inert row with the default cursor, so the affordance
     never promises something the click cannot deliver.

     The focus ring goes on the row, not on StyledFlex inside it: the row is
     what carries tabIndex, and StyledFlex clips to its own rounded corners
     while the menu is closed, which would crop an inner outline. */
  ${({ clickable }) =>
    clickable &&
    css`
      cursor: pointer;

      &:focus-visible {
        outline: none;
        border-radius: ${radius.md};
        box-shadow: ${shadow.focus};
      }
    `}

  ${({ isHeaderRow, index = 0 }) =>
    isHeaderRow
      ? css`
          margin-bottom: ${spacing.xs};
        `
      : css`
          animation: ${fadeInUp} ${duration.base} ${easing.entrance} both;
          animation-delay: ${stagger(index)};
        `}

  /* The row action menu is absolutely positioned INSIDE this row and drops
     below the trigger, so it overlaps the next row.

     It cannot escape on z-index alone: the fadeInUp animation above uses
     fill-mode 'both', which leaves a transform applied forever, and a
     transform creates a STACKING CONTEXT. That traps the menu's z-index
     inside this row, so the next row — later in DOM order, also at z-index
     auto — paints on top of it. Clicks then land on that row's cells, which
     have no handler, and Delete/Edit silently do nothing.

     Raising the row itself while its menu is open lifts the whole trapped
     subtree above its siblings. */
  ${({ isActionsOpen }) =>
    isActionsOpen &&
    css`
      position: relative;
      z-index: ${zIndex.dropdown};
    `}
`;

export const StyledFlex = styled.div<{
  isHead?: boolean;
  isActionsOpen?: boolean;
}>`
  display: flex;
  width: 100%;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.xs};
  /* Clips children to the rounded corners — but it also CLIPS THE ROW ACTION
     MENU, which is positioned absolutely inside this row and drops below it.
     The menu kept its layout box (so it measured correctly and looked present
     to a test) while painting nothing, and clicks fell through to the row
     underneath: Delete and Edit did nothing at all. Unclip while the menu is
     open; the corners only matter when it is closed. */
  overflow: ${({ isActionsOpen }) => (isActionsOpen ? 'visible' : 'hidden')};
  transition: ${transition.colors},
    transform ${duration.base} ${easing.standard};

  &:hover {
    background: ${palette.irisTintSoft};
    transform: translateY(-1px);
    box-shadow: ${shadow.sm};
  }

  ${down('md')} {
    flex-direction: column;
    align-items: stretch;
  }

  ${({ isHead }) =>
    isHead &&
    css`
      background: transparent;
      border: none;
      border-bottom: 1px solid ${palette.line};
      border-radius: 0;
      box-shadow: none;
      overflow: visible;

      &:hover {
        background: transparent;
        transform: none;
        box-shadow: none;
      }

      & > div {
        min-height: 48px;
      }

      & p {
        text-transform: uppercase;
        letter-spacing: ${typography.tracking.caps};
        font-weight: ${typography.weight.semibold};
        color: ${palette.slate};
      }

      ${down('md')} {
        display: none;
      }
    `}
`;

// total items width must be 100
export const ItemContainer = styled.div<{
  width?: number;
  isHeader?: boolean | undefined;
  isImage?: boolean | undefined;
  isActions?: boolean;
}>`
  box-sizing: border-box;
  width: ${({ width }) => (width ? `${width}%` : 'auto')};
  min-width: 0;
  min-height: ${({ isHeader }) => (isHeader ? '48px' : '64px')};
  display: flex;
  justify-content: ${({ isImage }) => (isImage ? 'center' : 'flex-start')};
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.xs} ${spacing.sm};
  /* The actions cell is the positioning parent for the row action menu, so it
     must not clip it. Every other cell keeps hidden for the text ellipsis. */
  overflow: ${({ isActions }) => (isActions ? 'visible' : 'hidden')};
  background: transparent;
  position: ${({ isActions }) => (isActions ? 'relative' : 'static')};

  & p {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
    min-width: 0;
  }

  &:nth-child(5) {
    justify-content: flex-end;
  }

  ${down('md')} {
    width: 100% !important;
    min-height: 0;
    justify-content: space-between;
    gap: ${spacing.sm};
    padding: ${spacing.sm};
    border-bottom: 1px solid ${palette.line};

    &::before {
      content: attr(data-label);
      flex: 0 0 auto;
      color: ${palette.slate};
      font-family: ${typography.ui};
      font-size: ${typography.size.xxs}px;
      font-weight: ${typography.weight.semibold};
      letter-spacing: ${typography.tracking.caps};
      text-transform: uppercase;
    }

    & p {
      white-space: normal;
      text-align: right;
    }

    &:first-of-type {
      justify-content: flex-start;
      gap: ${spacing.sm};

      &::before {
        display: none;
      }
      & p {
        text-align: left;
        font-weight: ${typography.weight.semibold};
      }
    }

    &:last-child {
      border-bottom: none;
    }

    &:nth-child(5) {
      justify-content: flex-end;
      border-bottom: none;

      &::before {
        display: none;
      }
    }
  }
`;

// Stacks the portfolio subtitle under the building name inside the first cell.
// ItemContainer is a horizontal, centre-aligned row, so a bare second element
// would land BESIDE the name; this is the column that puts it underneath.
/* WP-37 — THE ROW'S AFFORDANCE, AS A REAL CONTROL.
   The whole row used to be role="button" with its own tabIndex and Enter/Space
   handler. On a <tr> that role would replace the ROW role, so the affordance
   moved here. Styled to look exactly like the text it replaces — this is not a
   new button on the screen, it is the same click target with honest semantics.
   NO BACKTICKS IN THIS COMMENT - styled template literal. */
export const BuildingNameButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  /* 17px ON A PHONE, measured on Buildings at iPhone SE and iPad Mini by
     scripts/analytics/responsive.js. It is the row's primary control — the way
     into the building — and it was half the un-tappable floor, let alone the
     44px touchTarget. Only where there is a finger, so the dense desk row is
     unchanged; a flex line-height rather than a padded box, because padding
     here would move the name away from the address stacked under it. */
  ${touch} {
    display: inline-flex;
    align-items: center;
    min-height: ${layout.touchTarget};
  }
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  color: ${palette.ink};
  text-align: left;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: none;
    border-radius: ${radius.xs};
    box-shadow: ${shadow.focus};
  }
`;

export const BuildingNameStack = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: ${spacing.half};
  min-width: 0;
  max-width: 100%;
`;

// The portfolio a building sits in, under its name.
//
// A span, not a <p>, on purpose: ItemContainer's `down('md')` `&:first-of-type`
// block forces `font-weight: semibold` and `text-align: left` onto EVERY
// descendant paragraph, which would render this muted caption as a second title
// on a phone. Staying off the `p` selector also means the ellipsis rules have to
// be restated here.
export const BuildingPortfolioSubtitle = styled.span`
  display: block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.regular};
  letter-spacing: ${typography.tracking.snug};
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

export const Button = styled.button<{
  backgroundColor: string;
  textColor: string;
}>`
  -webkit-appearance: none;
  appearance: none;
  min-width: 92px;
  height: ${layout.touchTarget};
  padding: 0 ${spacing.sm};
  background-color: ${({ backgroundColor }) => backgroundColor};
  color: ${({ textColor }) => textColor};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  border: none;
  border-radius: ${radius.sm};
  transition: ${transition.colors},
    transform ${duration.fast} ${easing.standard};

  &:active {
    transform: scale(0.97);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

export const StyledInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: ${layout.fieldHeight};
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: 0 ${spacing.sm};
  border-radius: ${radius.xs};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  box-shadow: ${shadow.xs};
  transition: ${transition.colors};

  ::placeholder {
    color: ${palette.slateLight};
  }

  &:hover {
    border-color: ${palette.lineStrong};
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

export const AddNewPropertyFormContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  overflow-y: auto;
  padding: ${spacing.md} ${spacing.sm};

  ${up('md')} {
    padding: ${spacing.lg} ${spacing.md};
  }
`;

export const AddNewPropertyForm = styled.form`
  box-sizing: border-box;
  width: 100%;
  max-width: 480px;
  position: relative;
  display: flex;
  flex-direction: column;
  padding-bottom: ${spacing.sm};
`;

export const InputContainer = styled.div<{ isImageUpload?: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  margin-top: ${spacing.sm};
  gap: ${spacing.xs};

  ${({ isImageUpload }) =>
    isImageUpload &&
    css`
      align-items: center;
      justify-content: center;
      flex-direction: row;
      height: 88px;
      position: relative;
      gap: 0;
      border: 1px solid ${palette.line};
      border-radius: ${radius.xs};
      background: ${palette.surfaceMuted};
      transition: ${transition.colors};

      &:hover {
        border-color: ${palette.lagoon};
        background: ${palette.irisTintSoft};
      }
    `}

  input[type='file'] {
    height: 100%;
    width: 100%;
    z-index: 1;
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    cursor: pointer;
  }
`;

export const ImageUploadContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  position: relative;
`;

export const Label = styled.label<{
  isImageUpload?: boolean;
}>`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  letter-spacing: ${typography.tracking.snug};
  margin-bottom: ${({ isImageUpload }) => (isImageUpload ? '0' : spacing.xs)};
  margin-left: ${({ isImageUpload }) => (isImageUpload ? spacing.sm : '0')};
  color: ${palette.inkSoft};
  cursor: pointer;
`;

export const InteractiveFieldAction = styled.button`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  min-height: ${layout.touchTarget};
  margin-top: ${spacing.xs};
  padding: 0;
  border: 0;
  background: transparent;
  color: ${palette.lagoon};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    color: ${palette.irisDark};
  }
  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

`;

export const ErrorLabel = styled.label`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.medium};
  color: ${status.alert.fg};
  display: block;
  margin-top: ${spacing.xs};
`;

interface FieldErrorProps {
  /** Render (and unfold) the message when true; fold it away when false. */
  show?: boolean;
  /** For an input's aria-describedby. */
  id?: string;
  children: React.ReactNode;
}

/**
 * A field's validation line, UNFOLDING under it rather than popping in.
 *
 * InlineMessage (src/motion) animates the space open and closed, so the
 * fields below slide rather than jump, and the line leaves the same way when
 * the value is corrected. The wrapper carries role="alert" so the refusal is
 * announced; ErrorLabel inside is the same text style every form here uses.
 */
export const FieldError: React.FC<FieldErrorProps> = ({
  show = false,
  id,
  children,
}) => (
  <InlineMessage
    id={id}
    role="alert"
    message={show ? <ErrorLabel as="span">{children}</ErrorLabel> : undefined}
  />
);

// Quiet guidance under a field, rendered as a sibling after </InputContainer>
// exactly like ErrorLabel — same slot, same rhythm, just not an error.
export const FieldHint = styled.p`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.regular};
  letter-spacing: ${typography.tracking.snug};
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  display: block;
  margin: ${spacing.xs} 0 0;
`;

// Form-level failure. ErrorLabel above is per-field and sits beside the input
// it describes — which is invisible when the offending field is off-screen, so a
// refused save read as a button that did nothing. This one is the single place a
// save failure is always reported, at the top of the form.
export const FormBanner = styled.p`
  margin: 0 0 ${spacing.sm};
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${palette.alert};
  border-radius: ${radius.xs};
  background: ${palette.alertSoft};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.ink};
`;

/* ── Location picker ──────────────────────────────────────────────────────────
   The map replaced the address / city / country inputs. MapLibre renders the
   map into an element it owns, so these wrappers style the frame and leave the
   interior alone. */

export const MapFrame = styled.div`
  width: 100%;
  height: 280px;
  border-radius: ${radius.xs};
  border: 1px solid ${palette.line};
  overflow: hidden;
  background: ${palette.surfaceMuted};
  box-shadow: ${shadow.xs};

  /* Its own stacking context, at the bottom tier: the renderer positions its
     canvas and controls absolutely inside this box, and none of them may ever
     paint over the suggestion list above it or a modal. */
  position: relative;
  z-index: ${zIndex.base};

  ${down('md')} {
    height: 220px;
  }

  /* THE RENDERER'S OWN CONTROLS, BROUGHT UP TO A FINGER. MapLibre draws its
     zoom buttons at 29px and its attribution toggle at 24px. Under a touch
     pointer every control in this product is at least the 44px touch target,
     and a zoom button a thumb cannot hit is a map that cannot be zoomed on a
     phone - where two-finger gestures are already claimed for panning. */
  ${touch} {
    .maplibregl-ctrl-group button {
      width: ${layout.touchTarget};
      height: ${layout.touchTarget};
    }
    .maplibregl-ctrl-attrib.maplibregl-compact {
      min-height: ${layout.touchTarget};
      padding-right: ${layout.touchTarget};
    }
    .maplibregl-ctrl-attrib-button {
      width: ${layout.touchTarget};
      height: ${layout.touchTarget};
      background-position: center;
      background-repeat: no-repeat;
    }
  }

  /* The renderer puts its class on THIS element and sets its own font there;
     the attribution and the two-finger hint are text and take the product's. */
  &.maplibregl-map {
    font-family: ${typography.ui};
  }

  /* THE PIN CAN BE DRAGGED WITH ONE FINGER. The map itself leaves one-finger
     movement to the page (so the form still scrolls), and the pin inherited
     that: a finger on the pin scrolled the form while the pin tried to follow.
     The pin alone keeps the gesture. */
  .maplibregl-marker {
    touch-action: none;
  }

  /* KEYBOARD FOCUS. The canvas and the pin take focus, but this frame clips
     its overflow, so a ring drawn on either is cut off at the edge and the map
     showed no focus at all. The ring goes on the frame instead, whenever
     something inside it has keyboard focus. */
  .maplibregl-canvas:focus-visible,
  .maplibregl-marker:focus-visible {
    outline: none;
  }
  &:has(.maplibregl-canvas:focus-visible),
  &:has(.maplibregl-marker:focus-visible) {
    border-color: ${palette.iris};
    box-shadow: ${shadow.focus};
  }
  .maplibregl-ctrl button:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

// Wraps our OWN <input> plus the suggestion dropdown: a plain text box driven
// by /api/geo/autocomplete, so the field is identical to every other field on
// the page and stays something the user can type freely into.
//
// The address provider's attribution sits directly under it. Its free plan
// requires the credit to be a link, shown where its results are.
export const MapAttribution = styled.p`
  margin: ${spacing.half} 0 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};

  a {
    color: inherit;
    text-decoration: underline;
    border-radius: ${radius.xs};
    transition: ${transition.colors};
  }
  a:hover {
    color: ${palette.irisDeep};
  }
  a:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  /* A link is a control too: under a finger it gets the touch target. */
  ${touch} {
    a {
      display: inline-flex;
      align-items: center;
      min-height: ${layout.touchTarget};
    }
  }
`;

export const AutocompleteSlot = styled.div`
  position: relative;
  width: 100%;
`;

/* A motion root: it grows from the input it hangs off (overlay kind
   'popover', transform-origin set at the site) and shrinks back inside a
   Presence when the list closes. */
export const SuggestionList = m.div`
  position: absolute;
  top: calc(100% + ${spacing.half});
  left: 0;
  right: 0;
  z-index: ${zIndex.dropdown};
  max-height: 260px;
  overflow-y: auto;
  padding: ${spacing.half};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.xs};
  box-shadow: ${shadow.md};
`;

export const SuggestionItem = styled.button`
  display: block;
  width: 100%;
  min-height: ${layout.touchTarget};
  padding: ${spacing.xs} ${spacing.sm};
  border: 0;
  border-radius: ${radius.xs};
  background: none;
  text-align: left;
  cursor: pointer;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.ink};
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTintSoft};
  }

  /* HOVER AND FOCUS ARE NOT THE SAME EVENT.
     These were collapsed into one rule that also killed the outline, so the
     keyboard indicator on an address suggestion was a pale tint — 1.19:1
     against the surface — and indistinguishable from the row the mouse happens
     to be over. A keyboard user walking the list could not tell which
     suggestion they were about to choose. */
  &:focus-visible {
    background: ${palette.irisTintSoft};
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

// The resolved place, shown so the user can see exactly what will be saved
// before they submit — the map alone does not tell them which city Google
// decided this pin is in, and that value now reaches the reports.
export const LocationSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  margin-top: ${spacing.xs};
  padding: ${spacing.sm};
  border-radius: ${radius.xs};
  background: ${palette.surfaceMuted};
  border: 1px solid ${palette.line};
`;

export const LocationSummaryLine = styled.p<{ muted?: boolean }>`
  margin: 0;
  font-family: ${({ muted }) => (muted ? typography.mono : typography.ui)};
  font-size: ${typography.size.xxs}px;
  font-weight: ${({ muted }) =>
    muted ? typography.weight.regular : typography.weight.medium};
  color: ${({ muted }) => (muted ? palette.slate : palette.ink)};
  word-break: break-word;
`;

// Clears the pin. Sized to the 44px touch minimum via padding rather than a
// fixed height so it sits inline with the summary text.
export const LocationClearButton = styled.button`
  align-self: flex-start;
  margin-top: ${spacing.half};
  padding: ${spacing.xs} 0;
  min-height: ${layout.touchTarget};
  background: none;
  border: 0;
  cursor: pointer;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.medium};
  color: ${palette.lagoon};
  transition: color ${duration.fast} ${easing.standard};

  &:hover {
    color: ${palette.irisDeep};
  }
  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

`;

export const StyledBuildingInput = styled.input<{
  isImageUpload?: boolean;
  isCountryCodeInput?: boolean;
}>`
  box-sizing: border-box;
  width: 100%;
  height: ${layout.fieldHeight};
  display: flex;
  align-items: center;
  padding: 0 ${spacing.sm};
  border-radius: ${radius.xs};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  box-shadow: ${shadow.xs};
  transition: ${transition.colors};

  ${({ isCountryCodeInput }) => isCountryCodeInput && 'flex: 1 1 auto;'}

  ${({ isImageUpload }) =>
    isImageUpload &&
    css`
      height: 100%;
      padding: 0;
      border: none;
      box-shadow: none;
      background: transparent;
    `}

  ::placeholder {
    color: ${palette.slateLight};
  }

  &:hover {
    border-color: ${palette.lineStrong};
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

export const StyledBuildingSelect = styled.select`
  box-sizing: border-box;
  width: 100%;
  height: ${layout.fieldHeight};
  display: flex;
  align-items: center;
  padding: 0 ${spacing.sm};
  border-radius: ${radius.xs};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  cursor: pointer;
  box-shadow: ${shadow.xs};
  transition: ${transition.colors};

  &:hover {
    border-color: ${palette.lineStrong};
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

export const CountryCodeContainer = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${spacing.xs};
  width: 100%;
`;

export const CountryCodeSelect = styled.select`
  box-sizing: border-box;
  height: ${layout.fieldHeight};
  flex: 0 0 auto;
  min-width: 96px;
  max-width: 140px;
  padding: 0 ${spacing.sm};
  border-radius: ${radius.xs};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  cursor: pointer;
  box-shadow: ${shadow.xs};
  transition: ${transition.colors};

  &:hover {
    border-color: ${palette.lineStrong};
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

export const CountryCode = styled.div`
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
  margin-bottom: ${spacing.half};
  margin-right: ${spacing.xs};
`;

export const ButtonsContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.sm} ${spacing.md};
  border-top: 1px solid ${palette.line};
  background: ${palette.surface};

  ${down('sm')} {
    padding: ${spacing.sm};

    & > button {
      flex: 1;
    }
  }
`;

export const CancelButton = styled.button`
  -webkit-appearance: none;
  appearance: none;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  height: ${layout.touchTarget};
  padding: 0 ${spacing.md};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  color: ${palette.slate};
  cursor: pointer;
  transition: ${transition.colors},
    transform ${duration.fast} ${easing.standard};

  ${up('lg')} {
    height: ${layout.controlHeight};
  }

  &:hover {
    background: ${palette.irisTintSoft};
    border-color: ${palette.lagoon};
    color: ${palette.lagoon};
  }

  &:active {
    transform: scale(0.97);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

export const SaveButton = styled.button`
  -webkit-appearance: none;
  appearance: none;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  height: ${layout.touchTarget};
  padding: 0 ${spacing.md};
  border: none;
  border-radius: ${radius.sm};
  background: ${gradient.primary};
  color: ${palette.white};
  cursor: pointer;
  box-shadow: ${shadow.brand};
  transition: ${transition.colors},
    transform ${duration.fast} ${easing.standard};

  ${up('lg')} {
    height: ${layout.controlHeight};
  }

  & svg {
    transition: transform ${duration.fast} ${easing.standard};
  }

  &:hover {
    background: ${gradient.primaryHover};
    box-shadow: ${shadow.brandHover};
    transform: translateY(-1px);
  }

  &:hover svg {
    transform: translateX(2px);
  }

  &:active {
    transform: scale(0.97);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.brand}, ${shadow.focus};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;

export const ButtonTitle = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-style: normal;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  line-height: 1;
  margin: 0;
  color: inherit;
`;

// Click-away catcher for the row action menu.
//
// MUST be portalled to <body> (see dashboard-buildings.tsx). A `position: fixed`
// element takes its containing block from the nearest TRANSFORMED ancestor, not
// the viewport — and the row above it is permanently transformed by its
// fill-mode 'both' entry animation. Rendered inline it therefore covered only
// the row, not the screen, so clicking elsewhere never dismissed the menu.
//
// Sits below zIndex.dropdown so the open row (raised to exactly that) and its
// menu stay above it, while every other row falls beneath and dismisses.
export const ActionModalBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  z-index: ${zIndex.sticky};
`;

/* The row action menu. A motion root: it grows from the trigger's corner
   (overlay kind 'popover') and shrinks back inside a Presence when dismissed.
   No pointer-events trick on entrance — the e2e suite clicks Delete the
   instant the menu exists, and framer's transform never blocks a click. */
export const ActionModal = m.div`
  position: absolute;
  top: calc(100% + ${spacing.xs});
  right: ${spacing.xs};
  min-width: 184px;
  padding: ${spacing.xs};
  gap: ${spacing.xs};
  border-radius: ${radius.md};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  box-shadow: ${shadow.md};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  z-index: ${zIndex.dropdown};
  transform-origin: top right;
`;

export const RowActionTrigger = styled.button`
  -webkit-appearance: none;
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${layout.touchTarget};
  height: ${layout.touchTarget};
  padding: 0;
  border: none;
  border-radius: ${radius.circle};
  background: transparent;
  color: ${palette.slate};
  cursor: pointer;

  &:hover {
    background: ${palette.irisTintSoft};
    color: ${palette.lagoon};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const ButtonContainer = styled.button<{ isDelete?: boolean }>`
  -webkit-appearance: none;
  appearance: none;
  flex: 1;
  min-height: ${layout.buttonHeight};
  padding: 0 ${spacing.sm};
  border: none;
  border-radius: ${radius.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${transition.colors},
    transform ${duration.fast} ${easing.standard};
  background: ${({ isDelete }) =>
    isDelete ? palette.alertSoft : palette.irisTintSoft};
  color: ${({ isDelete }) => (isDelete ? status.alert.fg : palette.lagoon)};

  &:hover {
    ${({ isDelete }) =>
      isDelete
        ? `background: ${palette.alertStrong}; color: ${palette.white};`
        : `background: ${palette.irisTint}; color: ${palette.irisDark};`}
  }

  &:active {
    transform: scale(0.97);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ isDelete }) =>
      isDelete ? shadow.focusError : shadow.focus};
  }
`;

/* The building's cover thumbnail in the list, and the box it always gets.
   The 24px slot is reserved whether or not there is a photo — a row whose
   first cell grows 32px when its image arrives is a table that reflows as it
   loads. CoverImage is a motion root so the photo fades in when it has
   actually decoded (onLoad), not when the tag mounts. */
export const CoverSlot = styled.span`
  position: relative;
  display: inline-block;
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  margin-right: ${spacing.xs};
  border-radius: ${radius.xs};
  border: 1px solid ${palette.line};
  background: ${palette.irisTintSoft};
  overflow: hidden;
`;

export const CoverImage = m.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const StyledImage = styled.img<{ isAddNewPropertyForm?: boolean }>`
  width: 24px;
  height: 24px;
  display: inline-block;
  flex: 0 0 auto;
  object-fit: cover;
  border-radius: ${radius.xs};
  border: 1px solid ${palette.line};
  background: ${palette.surfaceMuted};
  margin-right: ${spacing.xs};

  width: ${({ isAddNewPropertyForm }) => isAddNewPropertyForm && '40px'};
  height: ${({ isAddNewPropertyForm }) => isAddNewPropertyForm && '40px'};
`;

// Currently UNUSED — building deletion goes through <ConfirmDialog>, which
// portals itself. If this is ever mounted again it MUST be wrapped in
// <ModalPortal> (see components/modal/modal-portal.tsx): rendered inline it
// inherits the screen's entrance animation as a containing block, so
// `position: fixed` covers the screen box rather than the viewport and the
// sticky header stays bright above the scrim.
export const DeleteModalBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: ${spacing.sm};
  background: ${palette.overlay};
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${zIndex.modal};
  animation: ${fadeIn} ${duration.fast} ${easing.standard} both;

  ${down('sm')} {
    align-items: flex-end;
    padding: 0;
  }
`;

export const DeleteModal = styled.div`
  box-sizing: border-box;
  background: ${palette.surface};
  width: 100%;
  max-width: 400px;
  padding: ${spacing.lg} ${spacing.md};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  border-radius: ${radius.md};
  box-shadow: ${shadow.lg};
  z-index: ${zIndex.modal};
  animation: ${scaleIn} ${duration.base} ${easing.spring} both;

  ${down('sm')} {
    max-width: 100%;
    border-radius: ${radius.md} ${radius.md} 0 0;
    padding: ${spacing.lg} ${spacing.sm} ${spacing.xl};
  }
`;

export const DeleteIconCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${radius.circle};
  background: ${palette.alertSoft};
  color: ${status.alert.fg};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${spacing.sm};
  animation: ${scaleIn} ${duration.base} ${easing.spring} both;

  & svg {
    width: 28px;
    height: 28px;
  }
`;

export const CancelConfirmButton = styled.button<{ isConfirm?: boolean }>`
  box-sizing: border-box;
  display: flex;
  min-height: ${layout.touchTarget};
  width: 100%;
  justify-content: center;
  align-items: center;
  margin-bottom: ${spacing.xs};
  padding: 0 ${spacing.sm};
  align-self: stretch;
  border-radius: ${radius.sm};
  cursor: pointer;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  transition: ${transition.colors},
    transform ${duration.fast} ${easing.standard};

  ${({ isConfirm }) =>
    isConfirm
      ? css`
          background: ${palette.alertStrong};
          color: ${palette.white};
          box-shadow: ${shadow.sm};

          &:hover {
            transform: translateY(-1px);
            box-shadow: ${shadow.md};
          }
          &:focus-visible {
            outline: none;
            box-shadow: ${shadow.focusError};
          }
        `
      : css`
          background: ${palette.surface};
          color: ${palette.slate};
          border: 1px solid ${palette.line};

          &:hover {
            background: ${palette.irisTintSoft};
            border-color: ${palette.lagoon};
            color: ${palette.lagoon};
          }
          &:focus-visible {
            outline: none;
            box-shadow: ${shadow.focus};
          }
        `}

  &:active {
    transform: scale(0.98);
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: ${spacing.sm};
  padding: ${spacing.xxl} ${spacing.md};
  margin-top: ${spacing.xs};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.xs};

  ${up('md')} {
    padding: ${spacing.xxxl} ${spacing.xl};
  }
`;

export const EmptyStateIcon = styled.div`
  width: 72px;
  height: 72px;
  border-radius: ${radius.lg};
  background: ${palette.irisTint};
  color: ${palette.lagoon};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${spacing.xs};
  animation: ${scaleIn} ${duration.slow} ${easing.spring} both;

  & svg {
    width: 36px;
    height: 36px;
  }
`;

/* ─────────────────  Edit-property, as a modal over the list  ───────────── */

// The card the building form sits in. The scrim is the shared one from
// dashboard-units.styled — one modal look for the whole dashboard.
//
// padding: 0 is deliberate. The form already supplies its own padding through
// AddNewPropertyFormContainer, and ButtonsContainer is a FULL-BLEED footer with
// a top border that has to reach both edges; card padding would inset that rule
// and leave the form double-padded.
//
// height is EXPLICIT rather than a max-height. DashboardContent is a
// `height: 100%` flex column with its own inner scroller, so inside an
// auto-height card that percentage resolves to auto — the inner scroller then
// never activates, the card grows past the viewport, and the scrim scrolls
// instead. An explicit height gives the intended shape: fixed header,
// scrolling body, footer pinned to the bottom.
//
// MOTION. Card and scrim are motion roots: the scrim fades (scrimMotion), the
// card grows from the centre (useOverlayMotion('modal') — a sheet rising from
// the bottom edge under `sm`), and both leave the way they came because the
// caller mounts them inside a Presence.
export const BuildingModalCard = m.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 560px;
  height: min(92vh, 760px);
  height: min(92dvh, 760px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: ${radius.md};
  background: ${palette.surface};
  box-shadow: ${shadow.lg};

  ${down('sm')} {
    height: calc(100vh - ${spacing.sm});
    height: calc(100dvh - ${spacing.sm});
    border-radius: ${radius.lg} ${radius.lg} 0 0;
  }
`;

// The scrim under the building modal. The same look as the unit dialogs'
// scrim (one modal look for the whole dashboard, at zIndex.modal so a confirm
// raised from inside sits above it at modalTop) — declared here rather than
// imported because it has to be a motion root that this screen's Presence can
// animate out.
export const ModalScrim = m.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.sm};
  background: ${palette.overlay};
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  overflow-y: auto;

  ${down('sm')} {
    align-items: flex-end;
    padding: 0;
  }
`;

// Wraps the form's fields so a single `disabled` can make every control inside
// read-only — including the location picker's inputs and the portfolio select,
// neither of which takes a disabled prop of its own. A fieldset is the only
// thing in HTML that disables a whole subtree.
//
// The reset matters: a fieldset defaults to a border, padding and a min-width of
// its content, all of which would resize the 480px form. `min-width: 0` is the
// one people forget, and it is what lets the column shrink on a phone.
export const FormFieldset = styled.fieldset`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
`;

// Says why the fields are inert, in the modal that shows them that way. Without
// it a read-only form reads as a broken one.
export const ReadOnlyNote = styled.p`
  box-sizing: border-box;
  margin: 0 0 ${spacing.sm} 0;
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${palette.irisTintSoft};
  color: ${palette.slate};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
`;

/* ──────────────  The units section inside the building modal  ───────────── */

// One column inside AddNewPropertyFormContainer holding the building form AND
// the units section, so the card keeps exactly ONE scroller.
//
// AddNewPropertyFormContainer is the scroller (flex: 1, overflow-y: auto) and
// BuildingModalCard has an explicit height, so anything mounted as a SIBLING of
// the form — between it and ButtonsContainer — would take its natural height,
// squeeze the form's scroller and give the card two independent scroll regions.
// Nesting instead means the units table simply scrolls with the fields.
//
// The 480px cap and width: 100% are the same values AddNewPropertyForm already
// carries, so the form renders at exactly the width it did before this wrapper
// existed. Nothing about AddNewPropertyFormContainer itself is changed — it is
// shared with the add-new-building page.
export const ModalScrollColumn = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 480px;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

// A hairline rule rather than a card: the section belongs to the building being
// edited, and a second card inside the modal would read as a second dialog.
export const UnitsSection = styled.section`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  margin-top: ${spacing.md};
  padding-top: ${spacing.md};
  border-top: 1px solid ${palette.line};
  animation: ${fadeIn} ${duration.base} ${easing.standard} both;
`;

export const UnitsSectionHead = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.sm};
  flex-wrap: wrap;
`;

export const UnitsSectionTitle = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.ink};
`;

// The counts line, and the reason an Add button is inert. Never a hex of its
// own: slateLight is the documented secondary-text token.
export const UnitsSectionNote = styled.p`
  margin: 0 0 ${spacing.sm} 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slateLight};
`;

/* ─────────────────  The building modal's units table  ────────────────── */

/* THE UNIT COLUMN KEEPS ROOM FOR A NAME. The units list's UnitName breaks
   anywhere (a 66-character label must never widen a 320px page), which also
   lets this narrower modal table shrink the Unit column to its min-content:
   54px, and "Stays fixture STB" stacked over three lines (Stays journey,
   J-S1.6, run s10191128a6c). About ten characters of floor, in em so it holds
   under text zoom; long names still wrap inside it. */
export const UnitCell = styled(Td)`
  min-width: 10em;
`;

/* THE ROW'S EDIT BUTTON IS A TARGET. The shared RowButton is dense on
   desktop by design (and 44px under a finger); inside this modal it measured
   61x17, under the 24px WCAG 2.5.8 floor. The compact control height is the
   design system's dense-utility size. */
export const UnitRowButton = styled(RowButton)`
  min-height: ${layout.compactControlHeight};
`;
